import {
  ContactDetails,
  ContactPicker,
  FormLayout,
  OrderDetailsInputProps,
} from '../common';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import {
  getFormHiddenComponent,
  getFormInputComponent,
  getFormTextComponent,
  showOverrideContactWarning,
} from '../common/Utils';
import { objectUtils } from '@/app/core/utils';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { useState } from 'react';
import { ClientContact, OrderDetails, asContact } from '@ssar-app/domain';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Text,
  Box,
  Grid,
  GridItem,
  SimpleGrid,
} from '@chakra-ui/react';
import { FormLabel } from '@ssar-app/uikit';
import { ObjectSchema } from 'yup';

export const OrderDetailsForm = (props: OrderDetailsInputProps) => {
  const { stepKey, validator } = props;
  const { contactType, clientAttributes } = props.moduleProps;

  const { isStepActive } = useStepContext();
  const [dataPartState] = useDataPart<OrderDetails>(stepKey);
  const [model, setModel] = useState<Partial<OrderDetails>>(
    {
      ...dataPartState?.data,
      tmsNumber: clientAttributes?.details?.accountNumber,
    } || {},
  );

  const formMethods = useForm<Partial<OrderDetails>>({
    defaultValues: model,
    mode: 'onChange',
    resolver: yupResolver(validator),
  });

  const {
    errors,
    control,
    formState,
    clearErrors,
    trigger,
    setValue,
    getValues,
  } = formMethods;

  const mergedModel: Partial<OrderDetails> = {
    ...dataPartState?.data,
    tmsNumber: clientAttributes?.details?.accountNumber,
  };

  const updateContact = (newClientContact: ClientContact): void => {
    const newContact = asContact(newClientContact);
    setModel({ ...model, contact: { ...newContact } });
    setValue('contact', newContact, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const contactSelected = (newClientContact: ClientContact): void => {
    const newContact = asContact(newClientContact);

    const { contact: existingContact } = getValues();
    if (existingContact && objectUtils.areAllPropsEmpty(existingContact)) {
      updateContact(newClientContact);
    } else if (!objectUtils.areEqual(existingContact, newContact, '_id')) {
      showOverrideContactWarning(newClientContact, (clientContact) => {
        updateContact(clientContact);
      });
    } else {
      updateContact(newClientContact);
    }
  };

  return (
    <Flex direction='column' w='full'>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={['name', 'tmsNumber', 'contact']}
        validator={validator as ObjectSchema}
        model={model}
        isDirty={formState.isDirty}
      />
      {isStepActive && (
        <form>
          <FormLayout header='Name & TMS Number'>
            <SimpleGrid columns={1}>
              <Box mb={4}>
                <FormControl
                  isInvalid={errors.name ? true : false}
                  isRequired={true}
                >
                  <Grid templateColumns='repeat(4,1fr)'>
                    <GridItem justifyContent='end' display='flex'>
                      <FormLabel marginTop={1}>Program Name :</FormLabel>
                    </GridItem>
                    <GridItem colSpan={3}>
                      {getFormInputComponent(
                        'name',
                        control,
                        mergedModel?.name || undefined,
                        false,
                        'Program Name',
                        'Name of the Program',
                        (value: string) => {
                          setModel({ ...model, name: value });
                        },
                      )}
                      <FormErrorMessage>
                        {errors.name?.message}
                      </FormErrorMessage>
                      <FormHelperText>
                        Note: Program Name must be at least 3 characters for
                        auto saving the program as a Draft
                      </FormHelperText>
                    </GridItem>
                  </Grid>
                </FormControl>
              </Box>
              <Box>
                <FormControl isInvalid={!!errors.tmsNumber} mb={6}>
                  <Grid templateColumns='repeat(4,1fr)'>
                    <GridItem justifyContent='end' display='flex'>
                      <FormLabel>TMS Number :</FormLabel>
                    </GridItem>
                    <GridItem colSpan={3}>
                      {getFormTextComponent(
                        'tmsNumber',
                        control,
                        clientAttributes?.details?.accountNumber || undefined,
                      )}
                      <FormErrorMessage>
                        {errors.tmsNumber?.message}
                      </FormErrorMessage>
                    </GridItem>
                  </Grid>
                </FormControl>
              </Box>
            </SimpleGrid>
            {getFormHiddenComponent(
              'doNotProcessDupsFlag',
              control,
              clientAttributes?.details?.doNotProcessDupsFlag || undefined,
            )}
            {getFormHiddenComponent(
              'listConvertErrorPctMax',
              control,
              clientAttributes?.details?.listConvertErrorPctMax || undefined,
            )}
            {getFormHiddenComponent(
              'noPinPctMax',
              control,
              clientAttributes?.details?.noPinPctMax || undefined,
            )}
          </FormLayout>
          <FormLayout
            header={
              <Flex direction='row' alignItems='baseline'>
                <Text fontSize='md' mr={6}>
                  Client Details
                </Text>
                <ContactPicker
                  inputContact={model.contact}
                  onContactSelected={(contact) => {
                    contactSelected(contact);
                  }}
                  contacts={clientAttributes?.contacts}
                  contactType={contactType}
                ></ContactPicker>
              </Flex>
            }
          >
            <SimpleGrid columns={1}>
              <Box>
                <ContactDetails
                  clearErrors={clearErrors}
                  isReadOnly={true}
                  propName='contact.'
                  control={formMethods.control}
                  errors={errors?.contact}
                  model={model.contact}
                />
              </Box>
            </SimpleGrid>
          </FormLayout>
        </form>
      )}
    </Flex>
  );
};
